import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import Select from './Select';
/**
 * A two-level cascading select.
 * Selecting a value in the primary dropdown loads options for the secondary dropdown.
 * The final value is a combination of both selections joined by `separator` (default `:`)
 * e.g. primary = "System", secondary = "Admin"  →  value = "System:Admin"
 * @export
 */
const CascadingSelect = ({ primaryOptions, primaryLabelField = 'label', primaryValueField = 'value', primaryPlaceholder, getSecondaryOptions, secondaryLabelField = 'label', secondaryValueField = 'value', secondaryPlaceholder, selected, onChange, separator = ':', className, fullWidth, unified, disabled, readOnly, }) => {
    const [primaryValue, setPrimaryValue] = useState('');
    const [secondaryValue, setSecondaryValue] = useState('');
    const [secondaryOptions, setSecondaryOptions] = useState([]);
    const [loadingSecondary, setLoadingSecondary] = useState(false);
    // Parse incoming `selected` into primary / secondary
    useEffect(() => {
        if (!selected) {
            setPrimaryValue('');
            setSecondaryValue('');
            return;
        }
        const idx = selected.indexOf(separator);
        if (idx === -1) {
            setPrimaryValue(selected);
            setSecondaryValue('');
        }
        else {
            setPrimaryValue(selected.slice(0, idx));
            setSecondaryValue(selected.slice(idx + separator.length));
        }
    }, [selected, separator]);
    // Reload secondary options whenever primary changes
    useEffect(() => {
        if (!primaryValue) {
            setSecondaryOptions([]);
            return;
        }
        let cancelled = false;
        setLoadingSecondary(true);
        getSecondaryOptions(primaryValue)
            .then(opts => { if (!cancelled)
            setSecondaryOptions(opts || []); })
            .catch(() => { if (!cancelled)
            setSecondaryOptions([]); })
            .finally(() => { if (!cancelled)
            setLoadingSecondary(false); });
        return () => { cancelled = true; };
    }, [primaryValue]); // eslint-disable-line react-hooks/exhaustive-deps
    const handlePrimaryChange = (value) => {
        setPrimaryValue(value);
        setSecondaryValue('');
        onChange(null, value, '');
    };
    const handleSecondaryChange = (value) => {
        setSecondaryValue(value);
        const combined = primaryValue && value ? `${primaryValue}${separator}${value}` : null;
        onChange(combined, primaryValue, value);
    };
    const isDisabledOrReadOnly = disabled || readOnly;
    return (_jsxs("div", { className: classNames('uxpcore_cascadingselect', { 'uxpcore_cascadingselect--full': fullWidth }, { 'uxpcore_cascadingselect--unified': unified }, className), style: { display: 'flex', gap: unified ? 0 : '0.5rem', width: fullWidth ? '100%' : undefined }, children: [_jsx(Select, { options: primaryOptions, labelField: primaryLabelField, valueField: primaryValueField, selected: primaryValue, onChange: handlePrimaryChange, placeholder: primaryPlaceholder, disabled: isDisabledOrReadOnly, fullWidth: fullWidth }), _jsx(Select, { options: secondaryOptions, labelField: secondaryLabelField, valueField: secondaryValueField, selected: secondaryValue, onChange: handleSecondaryChange, placeholder: loadingSecondary ? 'Loading…' : secondaryPlaceholder, disabled: isDisabledOrReadOnly || !primaryValue || loadingSecondary, fullWidth: fullWidth })] }));
};
export default CascadingSelect;
